import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import BeforeAfterSlider from './BeforeAfterSlider';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

/**
 * BeforeAfterShowcase — editorial gallery of MODARIA real before/after pairs.
 *
 * Layout:
 *  - Big featured slider on the left (active pair).
 *  - Right column with the headline + caption + thumbnail navigator.
 *  - Bottom strip of tiny thumbs to jump pairs.
 *
 * Pulls images from /api/uploads/looks/{before|after}/{NN}-{full|medium|thumb}.webp.
 */

const PAIR_IDS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13'];

const PAIR_META = {
  '01': { es: 'Editorial · cuero estructurado', en: 'Editorial · structured leather' },
  '02': { es: 'Studio · sastrería ligera',     en: 'Studio · soft tailoring' },
  '03': { es: 'Editorial · capa oversized',    en: 'Editorial · oversized coat' },
  '04': { es: 'Studio · jersey punto',         en: 'Studio · knitwear' },
  '05': { es: 'Editorial · vestido midi',      en: 'Editorial · midi dress' },
  '06': { es: 'Studio · denim premium',        en: 'Studio · premium denim' },
  '07': { es: 'Editorial · noche',             en: 'Editorial · evening' },
  '08': { es: 'Studio · outerwear',            en: 'Studio · outerwear' },
  '09': { es: 'Editorial · seda',              en: 'Editorial · silk' },
  '10': { es: 'Studio · workwear',             en: 'Studio · workwear' },
  '11': { es: 'Editorial · resort',            en: 'Editorial · resort' },
  '12': { es: 'Studio · jacquard',             en: 'Studio · jacquard' },
  '13': { es: 'Editorial · couture',           en: 'Editorial · couture' },
};

const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const buildSrc = (kind, id, size) => `${apiBase}/uploads/looks/${kind}/${id}-${size}.webp`;
const buildSrcSet = (kind, id) =>
  `${buildSrc(kind, id, 'medium')} 1000w, ${buildSrc(kind, id, 'full')} 1600w`;

export default function BeforeAfterShowcase() {
  const { content, locale } = useContent();
  const data = content.before_after || {};
  const [idx, setIdx] = useState(0);

  const t = (k, fb = '') => pickLocale(data[k], locale, fb);
  const pairs = (data.pair_ids?.length ? data.pair_ids : PAIR_IDS).slice(0, 13);
  const activeId = pairs[idx];

  const next = () => setIdx((i) => (i + 1) % pairs.length);
  const prev = () => setIdx((i) => (i - 1 + pairs.length) % pairs.length);

  const captionMeta = PAIR_META[activeId]?.[locale] || `Look ${activeId}`;

  return (
    <section
      id="before-after"
      data-testid="before-after-showcase"
      className="relative section bg-bg overflow-hidden"
    >
      <div className="container-x">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 md:mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-acid animate-pulse" />
              <span className="text-eyebrow text-acid">{t('eyebrow', 'ENTRADA → MODARIA')}</span>
            </div>
            <h2
              data-testid="before-after-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.98] text-ink"
            >
              {t('title', 'La misma prenda. Otra liga.')}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-ink-muted text-base md:text-lg leading-relaxed">
              {t('lead', 'Sube una foto plana, una de showroom o una de pasarela. MODARIA la convierte en una imagen editorial 2.560 × 3.840 lista para la página del producto, una campaña o un catálogo — con su Informe de Cumplimiento, su Autorización del Modelo y su huella ambiental firmados.')}
            </p>
            <p className="text-eyebrow text-ink-dim mt-3">
              {locale === 'es'
                ? 'EJEMPLOS DE DEMOSTRACIÓN · GENERADOS POR MODARIA. NO SON CAMPAÑAS DE CLIENTES.'
                : 'DEMO EXAMPLES · GENERATED BY MODARIA. NOT REAL BRAND CAMPAIGNS.'}
            </p>
          </div>
        </div>

        {/* Slider + side caption */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          <motion.div
            key={activeId}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-8"
          >
            <BeforeAfterSlider
              before={{
                src: buildSrc('before', activeId, 'medium'),
                srcSet: buildSrcSet('before', activeId),
                alt: `Input · ${captionMeta}`,
                label: locale === 'es' ? 'INPUT' : 'INPUT',
              }}
              after={{
                src: buildSrc('after', activeId, 'medium'),
                srcSet: buildSrcSet('after', activeId),
                alt: `MODARIA · ${captionMeta}`,
                label: 'MODARIA',
              }}
              aspect="3/4"
              locale={locale}
              testId={`ba-slider-${activeId}`}
            />
          </motion.div>

          <div className="lg:col-span-4 lg:pt-2">
            <div className="text-eyebrow text-ink-muted mb-3">
              {String(idx + 1).padStart(2, '0')} / {String(pairs.length).padStart(2, '0')}
            </div>
            <div className="font-display text-3xl md:text-4xl text-ink leading-tight mb-2" data-testid="ba-caption">
              {captionMeta}
            </div>
            <div className="text-eyebrow text-ink-dim mb-6">
              {locale === 'es' ? 'INPUT 736×1308 · MODARIA 2560×3840' : 'INPUT 736×1308 · MODARIA 2560×3840'}
            </div>

            <div className="flex items-center gap-2 mb-8">
              <button
                onClick={prev}
                aria-label={locale === 'es' ? 'Anterior' : 'Previous'}
                data-testid="ba-prev"
                className="w-10 h-10 flex items-center justify-center border border-line text-ink hover:text-acid hover:border-acid transition-colors magnetic"
              >
                <ArrowLeft size={14} />
              </button>
              <button
                onClick={next}
                aria-label={locale === 'es' ? 'Siguiente' : 'Next'}
                data-testid="ba-next"
                className="w-10 h-10 flex items-center justify-center border border-line text-ink hover:text-acid hover:border-acid transition-colors magnetic"
              >
                <ArrowRight size={14} />
              </button>
            </div>

            <p className="text-ink-muted text-sm leading-relaxed">
              {t('helper', 'Arrastra el divisor o pulsa la imagen para revelar el resultado. F para pantalla completa.')}
            </p>
          </div>
        </div>

        {/* Thumbnail strip */}
        <div className="mt-10 md:mt-14" data-testid="ba-thumbs">
          <div className="grid grid-cols-7 gap-2 sm:grid-cols-[repeat(13,minmax(0,1fr))]">
            {pairs.map((id, i) => (
              <button
                key={id}
                onClick={() => setIdx(i)}
                data-testid={`ba-thumb-${id}`}
                className={`relative aspect-[3/4] overflow-hidden border transition-all duration-300 ${
                  i === idx ? 'border-acid scale-100' : 'border-line/60 hover:border-line-strong opacity-70 hover:opacity-100'
                }`}
                aria-label={`Look ${id}`}
              >
                <img
                  src={buildSrc('after', id, 'thumb')}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-1 left-1 font-mono text-[9px] uppercase tracking-[0.18em] text-ink bg-bg/60 backdrop-blur-sm px-1">
                  {id}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
